import React, { useState, useEffect } from "react";
import { Form, Row, Col, Input, Select, TimePicker, InputNumber } from "antd";
import inputStyles from "../../../styles/Custom/Input.module.css";
import {
  dosageTiming,
  medicineFormOptions,
  medicineFrequency,
  medicineRoutes,
} from "../../../helpers/medicineKeys";
import dayjs from "dayjs";

const { Option } = Select;
const format = "hh A";

const defaultTimes = {
  Once_Daily: ["08 AM"],
  Twice_Daily: ["08 AM", "08 PM"],
  Thrice_Daily: ["08 AM", "01 PM", "08 PM"],
  Four_Times_Daily: ["08 AM", "01 PM", "04 PM", "08 PM"],
};

const AiMedicineListItemForm = ({ form, field }) => {
  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);

  /** -------------------------
   *  Initialize state from form values
   * -------------------------- **/
  useEffect(() => {
    // Get the current medication data from form
    const medications = form.getFieldValue("medications") || [];
    const currentMedication = medications[field.name];

    if (currentMedication) {
      setSelectedFrequency(currentMedication.frequency);
      setSelectedForm(currentMedication.form);

      // If frequency exists but times don't, set default times
      if (
        currentMedication.frequency &&
        !currentMedication.schedule?.times?.length
      ) {
        const defaultTimeValues = (
          defaultTimes[currentMedication.frequency] || []
        ).map((t) => dayjs(t, format));

        form.setFieldsValue({
          medications: {
            [field.name]: {
              ...currentMedication,
              schedule: { times: defaultTimeValues },
            },
          },
        });
      }
    }
  }, [form, field.name]);

  /** -------------------------
   *  Frequency change → update times
   * -------------------------- **/
  const handleFrequencyChange = (value) => {
    setSelectedFrequency(value);

    const defaultTimeValues = (defaultTimes[value] || []).map((t) =>
      dayjs(t, format)
    );

    // Get current form values
    const currentValues = form.getFieldValue("medications") || [];
    const currentMedication = currentValues[field.name] || {};

    // Update form with new times
    form.setFieldsValue({
      medications: {
        [field.name]: {
          ...currentMedication,
          frequency: value,
          schedule: { times: defaultTimeValues },
        },
      },
    });
  };

  /** -------------------------
   *  Time picker change
   * -------------------------- **/
  const handleTimeChange = (time, index) => {
    const currentValues = form.getFieldValue("medications") || [];
    const currentMedication = currentValues[field.name] || {};
    const currentTimes = currentMedication?.schedule?.times || [];

    const newTimes = [...currentTimes];
    newTimes[index] = time;

    form.setFieldsValue({
      medications: {
        [field.name]: {
          ...currentMedication,
          schedule: { times: newTimes },
        },
      },
    });
  };

  /** -------------------------
   *  Render time pickers
   * -------------------------- **/
  const renderTimePickers = () => {
    // Get frequency from form or state
    const currentFrequency =
      form.getFieldValue(["medications", field.name, "frequency"]) ||
      selectedFrequency;

    const count =
      {
        Once_Daily: 1,
        Twice_Daily: 2,
        Thrice_Daily: 3,
        Four_Times_Daily: 4,
      }[currentFrequency] || 0;

    if (count === 0) return null;

    return (
      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        {Array.from({ length: count }).map((_, index) => (
          <Col span={12} key={index}>
            <Form.Item
              name={[field.name, "schedule", "times", index]}
              rules={[{ required: true, message: "Required" }]}
            >
              <TimePicker
                inputReadOnly
                needConfirm={false}
                format={format}
                onChange={(t) => handleTimeChange(t, index)}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Row gutter={16} style={{ marginBottom: 12 }}>
      <Col span={8}>
        <Form.Item
          label="Medicine Name"
          name={[field.name, "name"]}
          rules={[{ required: true }]}
        >
          <Input placeholder="Name" className={inputStyles.mdInput} />
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item label="Drug" name={[field.name, "drug"]}>
          <Input placeholder="Drug" className={inputStyles.mdInput} />
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item label="Dose" name={[field.name, "dose"]}>
          <Input placeholder="Dose" className={inputStyles.mdInput} />
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item label="Form" name={[field.name, "form"]}>
          <Select
            className={inputStyles.mdSelect}
            onChange={(value) => setSelectedForm(value)}
            value={selectedForm}
          >
            {medicineFormOptions.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedForm === "Others" && (
          <Form.Item
            name={[field.name, "other_form"]}
            label="Other Form"
            rules={[{ required: true }]}
          >
            <Input placeholder="Enter form" className={inputStyles.mdInput} />
          </Form.Item>
        )}
      </Col>

      <Col span={8}>
        <Form.Item
          label="Frequency"
          name={[field.name, "frequency"]}
          rules={[{ required: true }]}
        >
          <Select
            className={inputStyles.mdSelect}
            onChange={handleFrequencyChange}
            value={selectedFrequency}
          >
            {medicineFrequency.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {renderTimePickers()}
      </Col>

      <Col span={8}>
        <Form.Item label="Route" name={[field.name, "route"]}>
          <Select className={inputStyles.mdSelect}>
            {medicineRoutes.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item label="Duration" name={[field.name, "duration"]}>
          <InputNumber
            className={inputStyles.mdNumberInput}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item label="Timing" name={[field.name, "dosage_timing"]}>
          <Select className={inputStyles.mdSelect}>
            {dosageTiming.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item label="Notes" name={[field.name, "comments"]}>
          <Input.TextArea
            rows={2}
            placeholder="Comments"
            className={inputStyles.mdInput}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default AiMedicineListItemForm;
